import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, CheckCircle2, Lock, Clock, ListChecks, ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export default function Quizzes() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/quizzes").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-muted-foreground">Loading quizzes…</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="quizzes-page">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-6 w-6 text-primary" />
        <h1 className="font-display font-bold text-3xl tracking-tight">Certification Quizzes</h1>
      </div>
      <p className="text-muted-foreground mt-1">
        Timed knowledge checks for each curriculum level. Score {" "}
        <span className="font-semibold text-foreground">70% or higher</span> to pass and earn XP.
      </p>

      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="quiz-overall-stats">
        <Trophy className="h-4 w-4 text-primary" />
        {data.stats.passed} of {data.stats.total} quizzes passed
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {data.quizzes.map((q) => (
          <div
            key={q.id}
            data-testid={`quiz-card-${q.id}`}
            className={`rounded-lg border bg-card p-5 transition-[transform,border-color] ${
              q.unlocked ? "border-border hover:border-primary/50 hover:-translate-y-0.5" : "border-border opacity-70"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded bg-primary/10 text-primary">
                Level {q.level}
              </div>
              {q.passed ? (
                <span className="flex items-center gap-1 text-xs text-success font-medium">
                  <CheckCircle2 className="h-4 w-4" /> Passed · {q.best_score}%
                </span>
              ) : q.attempts > 0 ? (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Best {q.best_score}%</span>
              ) : null}
            </div>

            <h3 className="font-display font-bold text-lg mt-3 tracking-tight">{q.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{q.description}</p>

            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4">
              <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {q.question_count} questions</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {Math.round(q.time_limit / 60)} min</span>
              <span className="font-mono">+{q.xp} XP</span>
            </div>

            {q.attempts > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                  <span>Best score</span>
                  <span className="font-mono">{q.best_score}% (pass {q.pass_pct}%)</span>
                </div>
                <Progress value={q.best_score} className="h-1.5" />
              </div>
            )}

            <div className="mt-4">
              {q.unlocked ? (
                <Button
                  className="w-full gap-2"
                  variant={q.passed ? "outline" : "default"}
                  data-testid={`quiz-start-${q.id}`}
                  onClick={() => navigate(`/quiz/${q.id}`)}
                >
                  {q.attempts > 0 ? (
                    <>
                      <RotateCcw className="h-4 w-4" /> {q.passed ? "Retake quiz" : "Try again"}
                    </>
                  ) : (
                    <>
                      Start quiz <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              ) : (
                <Button className="w-full gap-2" variant="secondary" disabled data-testid={`quiz-locked-${q.id}`}>
                  <Lock className="h-4 w-4" /> Complete a Level {q.level} lab to unlock
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
